import { createContext, useCallback, useContext, useRef, useState } from "react"
import type { ComponentProps } from "react"
import { CaretRightFilled, PauseOutlined } from "@ant-design/icons"
import classNames from "classnames"
import { Controls } from "./Controls"
import { usePlayback } from "./usePlayback"
import styles from "./index.css"

export const VideoDownloadPolicy = createContext(false)

export type VideoPlayerProps = ComponentProps<"video"> & {
  startTime?: number
  endTime?: number
  objectFit?: "contain" | "cover"
  timePrecision?: "seconds" | "centiseconds"
}

export const VideoPlayer = ({ src, ...props }: VideoPlayerProps) => (
  <VideoContent
    key={src}
    src={src}
    {...props}
  />
)

const VideoContent = ({
  className,
  style,
  ref,
  controls = false,
  controlsList,
  startTime,
  endTime,
  objectFit,
  timePrecision = "seconds",
  tabIndex,
  playsInline = true,
  preload = "metadata",
  onContextMenu,
  onEnded,
  onPlay,
  onPause,
  onError,
  onClick,
  onKeyDown,
  onLoadedMetadata,
  onDurationChange,
  onTimeUpdate,
  onVolumeChange,
  onSeeking,
  ...props
}: VideoPlayerProps) => {
  const playerRef = useRef<HTMLSpanElement>(null)
  const [actionError, setActionError] = useState("")
  const restrictDownload = useContext(VideoDownloadPolicy)
  const playback = usePlayback({ startTime, endTime, loop: props.loop })
  const videoRef = playback.ref
  const decorative =
    props["aria-hidden"] === true || props["aria-hidden"] === "true"
  const disabled = playback.failed || playback.invalidRange || !props.src
  const setVideoRef = useCallback(
    (element: HTMLVideoElement | null) => {
      videoRef.current = element
      const cleanup = typeof ref === "function" ? ref(element) : undefined
      if (ref && typeof ref !== "function") ref.current = element
      return () => {
        videoRef.current = null
        if (typeof cleanup === "function") cleanup()
        else if (typeof ref === "function") ref(null)
        else if (ref) ref.current = null
      }
    },
    [ref, videoRef],
  )

  return (
    <span
      ref={playerRef}
      className={classNames(
        styles.player,
        className,
        playback.playing && styles.playing,
      )}
      aria-hidden={props["aria-hidden"]}
      style={{
        width: props.width,
        height: props.height,
        aspectRatio: playback.aspectRatio || undefined,
        objectFit,
        ...style,
      }}
    >
      <video
        {...props}
        ref={setVideoRef}
        className={styles.video}
        controls={false}
        loop={props.loop && startTime === undefined && endTime === undefined}
        playsInline={playsInline}
        preload={preload}
        tabIndex={tabIndex ?? -1}
        controlsList={
          restrictDownload
            ? `${controlsList || ""} nodownload`.trim()
            : controlsList
        }
        onContextMenu={(event) => {
          if (restrictDownload) event.preventDefault()
          onContextMenu?.(event)
        }}
        onClick={(event) => {
          onClick?.(event)
          if (event.defaultPrevented || decorative) return
          event.stopPropagation()
          playback.toggle()
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if (
            event.defaultPrevented ||
            event.code !== "Space" ||
            event.altKey ||
            event.ctrlKey ||
            event.metaKey ||
            event.shiftKey ||
            event.nativeEvent.isComposing
          )
            return
          event.preventDefault()
          event.stopPropagation()
          if (!event.repeat && !decorative) playback.toggle()
        }}
        onEnded={(event) => {
          onEnded?.(event)
          playback.finish()
        }}
        onPlay={(event) => {
          playback.handlePlay()
          onPlay?.(event)
        }}
        onPause={(event) => {
          playback.handlePause()
          onPause?.(event)
        }}
        onError={(event) => {
          playback.handleError()
          onError?.(event)
        }}
        onLoadedMetadata={(event) => {
          playback.loadMetadata()
          onLoadedMetadata?.(event)
        }}
        onDurationChange={(event) => {
          playback.handleDurationChange()
          onDurationChange?.(event)
        }}
        onTimeUpdate={(event) => {
          playback.syncTime()
          onTimeUpdate?.(event)
        }}
        onVolumeChange={(event) => {
          playback.syncVolume()
          onVolumeChange?.(event)
        }}
        onSeeking={(event) => {
          playback.handleSeek()
          onSeeking?.(event)
        }}
      />
      {playback.ended && props.poster && !playback.failed ? (
        <img
          className={styles.cover}
          src={props.poster}
          alt=""
        />
      ) : null}
      {!decorative ? (
        <>
          <button
            type="button"
            className={styles.playButton}
            aria-label={playback.playing ? "暂停视频" : "播放视频"}
            disabled={disabled}
            onClick={(event) => {
              event.stopPropagation()
              // Keep consumer click overrides (e.g. opening a preview) on the video.
              videoRef.current?.click()
            }}
          >
            {playback.playing ? <PauseOutlined /> : <CaretRightFilled />}
          </button>
          {controls ? (
            <Controls
              duration={playback.length}
              elapsed={playback.elapsed}
              muted={playback.muted}
              volume={playback.volume}
              disabled={disabled}
              timePrecision={timePrecision}
              onFullscreen={() => {
                const player = playerRef.current
                if (!player) return
                setActionError("")
                const request =
                  document.fullscreenElement === player
                    ? document.exitFullscreen?.()
                    : player.requestFullscreen?.()
                if (!request) {
                  setActionError("当前浏览器不支持全屏播放")
                  return
                }
                void request.catch(() => setActionError("无法切换全屏，请重试"))
              }}
              onSeek={playback.seek}
              onVolumeChange={playback.changeVolume}
            />
          ) : null}
          {playback.failed ||
          playback.invalidRange ||
          playback.playFailed ||
          actionError ? (
            <span
              className={styles.error}
              role="status"
            >
              {actionError ||
                (playback.playFailed ? "播放失败，请重试" : "视频暂时无法播放")}
            </span>
          ) : null}
        </>
      ) : null}
    </span>
  )
}
